// components/Footer.jsx
// Category names come entirely from the API (DB has EN/HI/GU translations)
// No static hardcoded category data
import { useState, useEffect } from "react";
import { Logo, Icons } from "./UI";
import { useLang } from "../App";
import { categoryService } from "../services/api";
import cfg from "../config";

const Lnk = ({ label, onClick }) => (
  <button
    onClick={onClick}
    style={{
      display: "block",
      background: "none",
      border: "none",
      cursor: "pointer",
      fontFamily: "var(--sans)",
      fontSize: 14,
      color: "rgba(255,255,255,.5)",
      padding: "4px 0",
      textAlign: "left",
      transition: "color .2s",
    }}
    onMouseEnter={(e) => (e.currentTarget.style.color = "var(--mint)")}
    onMouseLeave={(e) => (e.currentTarget.style.color = "rgba(255,255,255,.5)")}
  >
    → {label}
  </button>
);

const Footer = ({ setPage }) => {
  const { lang, t } = useLang();
  // Start empty — load from API
  const [cats, setCats] = useState([]);

  useEffect(() => {
    // Always fetch from API — names are in DB (EN/HI/GU)
    categoryService
      .getAll()
      .then((apiCats) => {
        if (apiCats?.length > 0) setCats(apiCats);
      })
      .catch(() => {}); // fail silently, footer just shows no categories
  }, []);

  const footerDesc =
    cfg.footerDesc?.[lang] || cfg.footerDesc?.en || t("footerDesc");

  return (
    <footer
      style={{ background: "var(--ink)", color: "rgba(255,255,255,.65)" }}
    >
      <div className="wrap" style={{ padding: "60px var(--pad) 36px" }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))",
            gap: 40,
            marginBottom: 48,
          }}
        >
          {/* Brand */}
          <div>
            <div style={{ marginBottom: 14 }}>
              <Logo inverted />
            </div>
            <p
              style={{
                fontSize: 14,
                lineHeight: 1.85,
                color: "rgba(255,255,255,.45)",
                marginBottom: 22,
              }}
            >
              {footerDesc}
            </p>
            <button
              onClick={() => window.open(`https://wa.me/${cfg.whatsapp}`)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 7,
                background: "linear-gradient(135deg,#25d366,#128C7E)",
                color: "#fff",
                border: "none",
                borderRadius: 9,
                padding: "9px 18px",
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: "var(--sans)",
              }}
            >
              💬 WhatsApp Us
            </button>
          </div>

          {/* Quick links */}
          <div>
            <h4
              style={{
                color: "#fff",
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: 1.8,
                textTransform: "uppercase",
                marginBottom: 18,
              }}
            >
              {t("quickLinks")}
            </h4>
            {[
              ["home", t("home")],
              ["about", t("about")],
              ["products", t("products")],
              ["contact", t("contact")],
              ["privacy", t("privacy")],
              ["terms", t("terms")],
            ].map(([k, l]) => (
              <Lnk key={k} label={l} onClick={() => setPage(k)} />
            ))}
          </div>

          {/* Categories — names from DB via API, switch with language */}
          <div>
            <h4
              style={{
                color: "#fff",
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: 1.8,
                textTransform: "uppercase",
                marginBottom: 18,
              }}
            >
              {t("products")}
            </h4>
            {cats.length === 0 ? (
              // Loading state — show skeleton lines
              <>
                {[...Array(4)].map((_, i) => (
                  <div
                    key={i}
                    style={{
                      height: 14,
                      borderRadius: 4,
                      background: "rgba(255,255,255,.06)",
                      marginBottom: 10,
                      width: `${60 + i * 8}%`,
                    }}
                  />
                ))}
              </>
            ) : (
              cats.map((c) => (
                <button
                  key={c.slug || c.id}
                  onClick={() => setPage("products")}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    fontFamily: "var(--sans)",
                    fontSize: 14,
                    color: "rgba(255,255,255,.5)",
                    padding: "4px 0",
                    transition: "color .2s",
                    width: "100%",
                    textAlign: "left",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.color = "var(--mint)")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.color = "rgba(255,255,255,.5)")
                  }
                >
                  <span>{c.icon || "🌱"}</span>
                  {/* Name from DB — switches with lang immediately */}
                  {c.name[lang] || c.name.en}
                </button>
              ))
            )}
          </div>

          {/* Contact */}
          <div>
            <h4
              style={{
                color: "#fff",
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: 1.8,
                textTransform: "uppercase",
                marginBottom: 18,
              }}
            >
              {t("contact")}
            </h4>
            {[
              { icon: <Icons.Pin size={14} />, text: t("addr") },
              { icon: <Icons.Phone size={14} />, text: t("tel") },
              { icon: <Icons.Mail size={14} />, text: t("mail") },
            ].map((item, i) => (
              <div
                key={i}
                style={{ display: "flex", gap: 10, marginBottom: 12 }}
              >
                <span
                  style={{ color: "var(--sage)", marginTop: 2, flexShrink: 0 }}
                >
                  {item.icon}
                </span>
                <span style={{ fontSize: 13, lineHeight: 1.7 }}>
                  {item.text}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom bar */}
        <div
          style={{
            borderTop: "1px solid rgba(255,255,255,.07)",
            paddingTop: 22,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 10,
          }}
        >
          <p style={{ fontSize: 12, color: "rgba(255,255,255,.28)" }}>
            © {new Date().getFullYear()} {cfg.companyFull}. {t("rights")}.
          </p>
          <div style={{ display: "flex", gap: 18 }}>
            {[
              ["privacy", t("privacy")],
              ["terms", t("terms")],
            ].map(([k, l]) => (
              <button
                key={k}
                onClick={() => setPage(k)}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  fontFamily: "var(--sans)",
                  fontSize: 12,
                  color: "rgba(255,255,255,.28)",
                  transition: "color .2s",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.color = "rgba(255,255,255,.7)")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.color = "rgba(255,255,255,.28)")
                }
              >
                {l}
              </button>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
